import { useRef, useState } from "react";
import { Mic, Power } from "lucide-react";
import { useWin, SimBtn, Readout } from "./simKit";

const Hub = ({ online, total }) => (
  <div className="flex items-center gap-3 rounded-2xl bg-[#0A192F] text-white px-4 py-3" data-testid="luxeray-hub">
    <span className="relative w-10 h-10 rounded-xl bg-[#007A8A] grid place-items-center"><Power className="w-5 h-5" /><span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#3BB273] animate-pulse" /></span>
    <div><div className="font-bold text-sm">LuxeRay Smart Hub</div><div className="text-xs text-slate-300">{`${online}/${total} devices online · Wi-Fi + Zigbee`}</div></div>
  </div>
);

const Tile = ({ d, on, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`text-left rounded-xl border px-3 py-2 transition-[background-color,border-color] ${on ? "bg-[#E0F4F6] border-[#007A8A]" : "bg-white border-slate-200 hover:border-slate-300"}`}>
    <div className="text-xs text-slate-500">{d.room}</div>
    <div className="text-sm font-bold text-[#0A192F] leading-tight">{d.name}</div>
    <div className={`text-xs font-bold mt-0.5 ${on ? "text-[#007A8A]" : "text-slate-400"}`}>{on ? (d.on || "ON") : (d.off || "OFF")}{on && d.w ? ` · ${d.w} W` : ""}</div>
  </button>
);

const HOME = [
  ["lv-light", "Living", "Main light", 18], ["lv-fan", "Living", "Ceiling fan", 60], ["lv-ac", "Living", "AC", 1400], ["lv-tv", "Living", "Smart TV", 110], ["lv-cur", "Living", "Curtains", 0, "Open", "Closed"],
  ["bd-light", "Bedroom", "Light", 12], ["bd-ac", "Bedroom", "AC", 1200], ["bd-fan", "Bedroom", "Fan", 55],
  ["kt-light", "Kitchen", "Light", 15], ["kt-exh", "Kitchen", "Exhaust fan", 40], ["bt-gey", "Bathroom", "Geyser", 2000],
  ["en-lock", "Entrance", "Door lock", 0, "Locked", "Unlocked"], ["en-cam", "Entrance", "CCTV camera", 8], ["en-porch", "Entrance", "Porch light", 10], ["ut-pump", "Utility", "Water pump", 750],
].map(([id, room, name, w, on, off]) => ({ id, room, name, w, on, off }));
const SCENES = {
  "Good Morning": { "lv-cur": 1, "kt-light": 1, "bt-gey": 1, "en-porch": 0, "bd-ac": 0, "en-lock": 0, "ut-pump": 1 },
  Away: Object.fromEntries(HOME.map((d) => [d.id, d.id === "en-lock" || d.id === "en-cam" ? 1 : 0])),
  Night: Object.fromEntries(HOME.map((d) => [d.id, ["bd-ac", "en-lock", "en-cam", "en-porch"].includes(d.id) ? 1 : 0])),
};
const ROOMS = { Living: [10, 10, 230, 150], Bedroom: [250, 10, 140, 150], Kitchen: [400, 10, 110, 80], Bathroom: [400, 95, 110, 65], Entrance: [10, 170, 300, 80], Utility: [320, 170, 190, 80] };

export const HomeHubSim = ({ onSuccess }) => {
  const [st, setSt] = useState({ "lv-light": 1, "lv-tv": 1, "en-cam": 1, "en-lock": 1 });
  const [ran, setRan] = useState({});
  const [manual, setManual] = useState(false);
  const watts = HOME.reduce((s, d) => s + (st[d.id] ? d.w : 0), 0);
  useWin(manual && Object.keys(ran).length === 3, onSuccess);
  return (
    <div className="space-y-4">
      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-4">
        <svg viewBox="0 0 520 260" className="w-full rounded-2xl border border-slate-200 bg-[#F7FAFF]" data-testid="home-floorplan">
          {Object.entries(ROOMS).map(([r, [x, y, w, h]]) => {
            const ds = HOME.filter((d) => d.room === r), lit = ds.some((d) => st[d.id] && d.name.toLowerCase().includes("light"));
            return <g key={r}><rect x={x} y={y} width={w} height={h} rx="10" fill={lit ? "#FFF4D6" : "#fff"} stroke="#CBD5E1" strokeWidth="2" style={{ transition: "fill .4s" }} /><text x={x + 10} y={y + 20} fontSize="13" fontWeight="700" fill="#0A192F">{r}</text>
              {ds.map((d, i) => <circle key={d.id} cx={x + 18 + (i % 5) * 26} cy={y + 44 + Math.floor(i / 5) * 26} r="9" fill={st[d.id] ? "#007A8A" : "#CBD5E1"} style={{ cursor: "pointer" }} onClick={() => { setSt({ ...st, [d.id]: st[d.id] ? 0 : 1 }); setManual(true); }} data-testid={`home-dot-${d.id}`} />)}</g>;
          })}
        </svg>
        <div className="space-y-3">
          <Hub online={15} total={15} />
          <div className="grid grid-cols-3 gap-2">{Object.keys(SCENES).map((n) => (
            <button key={n} onClick={() => { setSt((p) => ({ ...p, ...SCENES[n] })); setRan((r) => ({ ...r, [n]: true })); }} data-testid={`scene-${n.toLowerCase().replace(" ", "-")}`}
              className={`rounded-xl px-2 py-3 text-sm font-bold border ${ran[n] ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white border-slate-200 hover:border-[#007A8A]"}`}>{n}</button>))}</div>
          <Readout items={[["Live power", `${watts} W`, watts < 100 ? "good" : ""], ["Scenes run", `${Object.keys(ran).length} / 3`, Object.keys(ran).length === 3 ? "good" : ""], ["Manual control", manual ? "Done" : "Tap a device", manual ? "good" : ""]]} />
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">{HOME.map((d) => <Tile key={d.id} d={d} on={!!st[d.id]} onClick={() => { setSt({ ...st, [d.id]: st[d.id] ? 0 : 1 }); setManual(true); }} testid={`home-dev-${d.id}`} />)}</div>
    </div>
  );
};

const HOTEL = [
  ["Entry", ["Keycard slot", "Corridor light", "Door lock", "Doorbell", "DND / MUR panel"]],
  ["Bedroom", ["Main light", "Left bedside lamp", "Right bedside lamp", "AC", "Curtains", "Sheer curtains", "Smart TV", "Minibar", "In-room safe", "Occupancy sensor", "Smart speaker", "Night lamp"]],
  ["Bathroom", ["Bath light", "Mirror defogger", "Exhaust fan", "Geyser", "Leak sensor"]],
  ["Balcony & desk", ["Balcony light", "Balcony door sensor", "Desk lamp"]],
].flatMap(([room, names]) => names.map((name) => ({ id: name.toLowerCase().replace(/[^a-z]+/g, "-"), room, name })));
const ALWAYS = ["door-lock", "occupancy-sensor", "leak-sensor", "balcony-door-sensor", "in-room-safe", "minibar"];
const MODES = {
  vacant: (h) => Object.fromEntries(h.map((d) => [d.id, ALWAYS.includes(d.id) ? 1 : 0])),
  checkin: () => ({ "corridor-light": 1, "main-light": 1, "left-bedside-lamp": 1, "right-bedside-lamp": 1, ac: 1, curtains: 1, "smart-tv": 1, "smart-speaker": 1, geyser: 1, "dnd-mur-panel": 0 }),
  sleep: () => ({ "main-light": 0, "left-bedside-lamp": 0, "right-bedside-lamp": 0, "corridor-light": 0, "smart-tv": 0, curtains: 0, "sheer-curtains": 0, "night-lamp": 1, ac: 1 }),
};

export const HotelSim = ({ onSuccess }) => {
  const [st, setSt] = useState(() => MODES.vacant(HOTEL));
  const [flags, setFlags] = useState({});
  const [status, setStatus] = useState("Vacant · eco mode (AC 28 °C)");
  const [card, setCard] = useState(false);
  const flag = (k) => setFlags((f) => ({ ...f, [k]: true }));
  const act = {
    checkin: () => { setSt((p) => ({ ...p, ...MODES.checkin() })); setStatus("Guest checked in · welcome scene, AC 24 °C"); flag("checkin"); },
    card: () => {
      if (!flags.checkin) { setStatus("No guest booked — check in first."); return; }
      const v = !card; setCard(v); setSt((p) => ({ ...p, "keycard-slot": v ? 1 : 0, ...(v ? { "main-light": 1, ac: 1 } : { "main-light": 0, "smart-tv": 0, "corridor-light": 0, "left-bedside-lamp": 0, "right-bedside-lamp": 0, geyser: 0 }) }));
      setStatus(v ? "Keycard in · room power ON" : "Keycard out · lights & TV off to save energy"); if (v) flag("card");
    },
    dnd: () => { setSt((p) => ({ ...p, "dnd-mur-panel": p["dnd-mur-panel"] ? 0 : 1, doorbell: p["dnd-mur-panel"] ? 1 : 0 })); setStatus("Do-Not-Disturb toggled · doorbell muted"); flag("dnd"); },
    sleep: () => { setSt((p) => ({ ...p, ...MODES.sleep() })); setStatus("Sleep mode · night lamp, curtains closed, AC 25 °C"); flag("dnd"); },
    checkout: () => {
      if (!flags.card) { setStatus("Insert the keycard during the stay before checking out."); return; }
      setSt({ ...MODES.vacant(HOTEL), "dnd-mur-panel": 1 }); setCard(false); setStatus("Checked out · room powered down · housekeeping alerted (MUR)"); flag("checkout");
    },
  };
  const won = flags.checkin && flags.card && flags.dnd && flags.checkout;
  useWin(!!won, onSuccess);
  const onCount = HOTEL.filter((d) => st[d.id]).length;
  return (
    <div className="space-y-4">
      <div className="grid lg:grid-cols-[1fr_1.3fr] gap-4">
        <div className="space-y-3">
          <Hub online={25} total={25} />
          <div className="grid grid-cols-2 gap-2">
            {[["checkin", "1 · Check-in"], ["card", card ? "2 · Remove keycard" : "2 · Insert keycard"], ["dnd", "3a · Toggle DND"], ["sleep", "3b · Sleep mode"], ["checkout", "4 · Check-out"]].map(([k, l]) => (
              <SimBtn key={k} tone={flags[k] || (k === "sleep" && flags.dnd) ? "teal" : "light"} onClick={act[k]} testid={`hotel-${k}`}>{l}</SimBtn>))}
          </div>
          <p className="rounded-xl bg-[#F0F4F8] px-3 py-2 text-sm" data-testid="hotel-status">{status}</p>
          <Readout items={[["Devices ON", `${onCount} / 25`], ["Guest journey", won ? "Complete!" : `${["checkin", "card", "dnd", "checkout"].filter((k) => flags[k]).length} / 4`, won ? "good" : ""]]} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="hotel-devices">{HOTEL.map((d) => <Tile key={d.id} d={d} on={!!st[d.id]} onClick={() => setSt({ ...st, [d.id]: st[d.id] ? 0 : 1 })} testid={`hotel-dev-${d.id}`} />)}</div>
      </div>
    </div>
  );
};

const VDEV = { "living room light": "Living light", "bedroom light": "Bedroom light", fan: "Fan", ac: "AC", curtains: "Curtains", tv: "TV" };
export const VoiceHomeSim = ({ onSuccess }) => {
  const [st, setSt] = useState({ "Living light": false, "Bedroom light": false, Fan: false, AC: false, Curtains: false, TV: false, acTemp: 26, fanSpeed: 0 });
  const [q, setQ] = useState("");
  const [log, setLog] = useState([]);
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const Rec = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const ok = log.filter((l) => l.ok).length;
  useWin(ok >= 4, onSuccess);
  const run = (text) => {
    const t = text.toLowerCase().trim();
    if (!t) return;
    let dev = Object.keys(VDEV).find((k) => t.includes(k)) || (t.includes("light") ? (t.includes("bed") ? "bedroom light" : "living room light") : null);
    const num = t.match(/(\d{2})/)?.[1];
    let reply = "Sorry, I didn't catch a device. Try “turn on bedroom light”.", good = false, n = { ...st };
    if (dev) {
      const name = VDEV[dev];
      if (name === "AC" && num) { n.AC = true; n.acTemp = Math.max(16, Math.min(30, Number(num))); reply = `AC set to ${n.acTemp} °C.`; good = true; }
      else if (name === "Fan" && /speed|\b[1-5]\b/.test(t)) { const sp = Number(t.match(/\b([1-5])\b/)?.[1] || 3); n.Fan = true; n.fanSpeed = sp; reply = `Fan speed ${sp}.`; good = true; }
      else if (/(turn on|switch on|open|start|\bon\b)/.test(t)) { n[name] = true; reply = name === "Curtains" ? "Opening the curtains." : `${name} is ON.`; good = true; }
      else if (/(turn off|switch off|close|stop|\boff\b)/.test(t)) { n[name] = false; reply = name === "Curtains" ? "Closing the curtains." : `${name} is OFF.`; good = true; }
      else reply = `What should I do with the ${name}? Say on, off or a number.`;
    }
    setSt(n); setLog((l) => [...l, { q: text, a: reply, ok: good }]); setQ("");
  };
  const listen = () => {
    if (!Rec) return;
    const r = new Rec(); r.lang = "en-IN"; r.interimResults = false; recRef.current = r;
    r.onresult = (e) => run(e.results[0][0].transcript); r.onend = () => setListening(false); r.onerror = () => setListening(false);
    setListening(true); r.start();
  };
  return (
    <div className="grid lg:grid-cols-[1fr_1.1fr] gap-4">
      <div className="space-y-3">
        <Hub online={6} total={6} />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="voice-devices">{Object.values(VDEV).map((name) => (
          <div key={name} className={`rounded-xl border px-3 py-2 ${st[name] ? "bg-[#E0F4F6] border-[#007A8A]" : "bg-white border-slate-200"}`} data-testid={`voice-dev-${name.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="text-sm font-bold">{name}</div>
            <div className={`text-xs font-bold ${st[name] ? "text-[#007A8A]" : "text-slate-400"}`}>{st[name] ? (name === "AC" ? `ON · ${st.acTemp} °C` : name === "Fan" && st.fanSpeed ? `ON · speed ${st.fanSpeed}` : name === "Curtains" ? "Open" : "ON") : name === "Curtains" ? "Closed" : "OFF"}</div>
          </div>))}</div>
        <Readout items={[["Successful commands", `${ok} / 4`, ok >= 4 ? "good" : ""]]} />
      </div>
      <div className="rounded-2xl border border-slate-200 bg-[#F7FAFF] p-4 flex flex-col">
        <div className="flex-1 space-y-2 max-h-64 overflow-y-auto" data-testid="voice-log">{log.map((l, i) => (
          <div key={i}><div className="ml-auto w-fit max-w-[85%] rounded-xl bg-[#007A8A] text-white px-3 py-1.5 text-sm">{l.q}</div>
            <div className={`mt-1 w-fit max-w-[85%] rounded-xl px-3 py-1.5 text-sm border ${l.ok ? "bg-white border-slate-200" : "bg-[#FFE4EA] border-[#FFC2D0]"}`}>{l.a}</div></div>))}
          {!log.length && <p className="text-sm text-slate-500">Try: “turn on bedroom light”, “set AC to 24”, “fan speed 3”, “close curtains”.</p>}</div>
        <form onSubmit={(e) => { e.preventDefault(); run(q); }} className="flex gap-2 mt-3">
          {Rec && <button type="button" onClick={listen} className={`w-10 h-10 shrink-0 rounded-full grid place-items-center ${listening ? "bg-[#FF2E63] text-white animate-pulse" : "bg-[#0A192F] text-white"}`} aria-label="Speak" data-testid="voice-mic-btn"><Mic className="w-4 h-4" /></button>}
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type a command…" className="flex-1 rounded-full border border-slate-200 px-4 text-sm" data-testid="voice-input" />
          <SimBtn testid="voice-send">Send</SimBtn>
        </form>
        {!Rec && <p className="text-xs text-slate-500 mt-2">Voice input works in Chrome/Edge — typing works everywhere.</p>}
      </div>
    </div>
  );
};
